import type {
    IComponentCodeBlock,
    IComponentCodeBlockFields
} from '@contentful/types';
import { COMPONENT_CODE_BLOCK } from '../../../contentful/content-types';
import { SupportedLanguages } from '@atlaskit/code';

// contentful-typescript-codegen assumes the REST API, which has a different response format from the GraphQL, is used
// The 'Gql' prefix is used to imply typing that is specific to the GraphQL responses
export interface GqlComponentCodeBlock
    extends Pick<IComponentCodeBlockFields, 'title' | 'code' | 'language'> {
    readonly sys: Pick<IComponentCodeBlock['sys'], 'id'>;
    readonly type: Capitalize<typeof COMPONENT_CODE_BLOCK>;
    readonly __typename: Capitalize<typeof COMPONENT_CODE_BLOCK>;
}

/**
 * Maps `componentCodeBlock`'s language fields to Atlaskit's supported languages.
 * @see https://atlassian.design/components/code/code-block/code
 */
const CODE_LANGUAGE_MAP: Record<
    IComponentCodeBlockFields['language'],
    SupportedLanguages | undefined
> = {
    ABAP: 'abap',
    ActionScript: 'actionscript',
    AppleScript: 'applescript',
    Bash: 'shell',
    'C#': 'csharp',
    'C++': 'cpp',
    ColdFusion: 'coldfusion',
    CSS: 'css',
    // Not explicity supported, going with the closest language
    Delphi: 'pascal',
    Diff: 'diff',
    Erlang: 'erlang',
    Groovy: 'groovy',
    HTML: 'html',
    'Java FX': 'java',
    Java: 'java',
    JavaScript: 'javascript',
    JSON: 'json',
    Other: undefined,
    Perl: 'perl',
    PHP: 'php',
    'Plain text': 'plaintext',
    PowerShell: 'powershell',
    Python: 'python',
    Ruby: 'ruby',
    Sass: 'sass',
    Scala: 'scala',
    SQL: 'sql',
    'Visual Basic': 'visualbasic',
    XML: 'xml',
    YAML: 'yaml'
};

export const getCodeLanguage = (
    input: IComponentCodeBlockFields['language']
): SupportedLanguages | undefined => {
    return CODE_LANGUAGE_MAP?.[input];
};
